import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Package, CheckCircle2, Truck, Clock, Star, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { useSettings } from '../contexts/SettingsContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatPrice = (p) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(p);

const statusConfig = {
  pending: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-800', icon: Clock, step: 0 },
  approved: { label: 'Aprobado', color: 'bg-blue-100 text-blue-800', icon: CheckCircle2, step: 1 },
  production: { label: 'En Produccion', color: 'bg-purple-100 text-purple-800', icon: Star, step: 2 },
  delivery: { label: 'En Entrega', color: 'bg-orange-100 text-orange-800', icon: Truck, step: 3 },
  delivered: { label: 'Entregado', color: 'bg-green-100 text-green-800', icon: CheckCircle2, step: 4 },
};

const timelineSteps = [
  { key: 'pending', label: 'Pedido recibido' },
  { key: 'approved', label: 'Aprobado' },
  { key: 'production', label: 'En produccion' },
  { key: 'delivery', label: 'En camino' },
  { key: 'delivered', label: 'Entregado' },
];

export default function OrderTrackingPage() {
  const { whatsappNumber } = useSettings();
  const [searchParams] = useSearchParams();
  const [code, setCode] = useState(searchParams.get('code') || '');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (searchParams.get('code')) handleSearch(null, searchParams.get('code'));
  }, []);

  const handleSearch = async (e, directCode) => {
    if (e) e.preventDefault();
    const searchCode = directCode || code;
    if (!searchCode.trim()) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API}/orders/track/${searchCode.trim().toUpperCase()}`);
      setOrder(data);
      setError('');
    } catch (err) {
      console.error(err);
      setError('No se encontró ningún pedido con ese código de seguimiento.');
      setOrder(null);
    } finally {
      setLoading(false);
    }
  };

  const currentStep = order ? (statusConfig[order.status]?.step || 0) : 0;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16 animate-fade-in" data-testid="tracking-page">
      <div className="text-center mb-10">
        <Package className="w-12 h-12 mx-auto text-[#a0d5ba] mb-4" />
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#46371c] mb-3">Como va mi pedido?</h1>
        <p className="font-body text-[#75664d]">Ingresa tu codigo de seguimiento</p>
      </div>

      <form onSubmit={handleSearch} className="flex gap-2 max-w-md mx-auto mb-12">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#75664d]" />
          <input
            data-testid="tracking-input"
            value={code}
            onChange={e => setCode(e.target.value)}
            placeholder="Ej: KR-A1B2C3D4"
            className="w-full h-12 pl-10 pr-4 rounded-full border border-[#e5dfd3] bg-white text-[#46371c] font-body text-sm focus:outline-none focus:ring-2 focus:ring-[#a0d5ba]"
          />
        </div>
        <Button type="submit" disabled={loading} className="bg-[#46371c] text-[#fcfbf9] hover:bg-[#2d2312] rounded-full px-6 h-12 font-body" data-testid="tracking-search-btn">
          {loading ? '...' : 'Buscar'}
        </Button>
      </form>

      {error && <p className="text-center text-red-500 font-body text-sm mb-8" data-testid="tracking-error">{error}</p>}

      {/* Sin backend: mostrar info de contacto WhatsApp */}
      {!order && !loading && code && (
        <Card className="p-8 border border-[#e5dfd3] rounded-2xl text-center" data-testid="tracking-whatsapp-fallback">
          <MessageCircle className="w-10 h-10 mx-auto text-[#25D366] mb-4" />
          <h3 className="font-heading text-xl font-semibold text-[#46371c] mb-2">Consulta el estado de tu pedido</h3>
          <p className="font-body text-sm text-[#75664d] mb-6">Escribenos por WhatsApp con tu codigo <span className="font-semibold text-[#46371c]">{code}</span> y te daremos informacion al instante.</p>
          <a href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(`Hola Keiks! Quiero saber el estado de mi pedido ${code}`)}`} target="_blank" rel="noopener noreferrer">
            <Button className="bg-[#25D366] text-white hover:bg-[#20bd5a] rounded-full px-8 font-body" data-testid="tracking-whatsapp-btn">
              <MessageCircle className="w-4 h-4 mr-2" /> Consultar por WhatsApp
            </Button>
          </a>
        </Card>
      )}

      {order && (
        <Card className="p-6 sm:p-8 border border-[#e5dfd3] rounded-2xl" data-testid="tracking-result">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="font-body text-xs text-[#75664d]">Codigo</p>
              <p className="font-heading text-2xl font-bold text-[#46371c]">{order.tracking_code}</p>
            </div>
            <Badge className={statusConfig[order.status]?.color}>{statusConfig[order.status]?.label}</Badge>
          </div>

          {/* Timeline */}
          <div className="mb-8" data-testid="order-timeline">
            <div className="flex items-center justify-between relative">
              <div className="absolute top-4 left-0 right-0 h-0.5 bg-[#e5dfd3]" />
              <div className="absolute top-4 left-0 h-0.5 bg-[#a0d5ba] transition-all" style={{ width: `${(currentStep / 4) * 100}%` }} />
              {timelineSteps.map((step, i) => (
                <div key={step.key} className="relative z-10 flex flex-col items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${i <= currentStep ? 'bg-[#a0d5ba] text-[#46371c]' : 'bg-[#e5dfd3] text-[#75664d]'}`}>
                    {i < currentStep ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                  </div>
                  <span className="font-body text-xs text-[#75664d] mt-2 text-center max-w-[60px]">{step.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 font-body text-sm">
            <div><span className="text-[#75664d]">Cliente:</span> <span className="text-[#46371c] font-medium">{order.customer_name}</span></div>
            <div><span className="text-[#75664d]">Telefono:</span> <span className="text-[#46371c] font-medium">{order.customer_phone}</span></div>
            <div><span className="text-[#75664d]">Entrega:</span> <span className="text-[#46371c] font-medium">{order.delivery_type === 'delivery' ? 'Domicilio' : 'Recoger en tienda'}</span></div>
            {order.store_name && <div><span className="text-[#75664d]">Sede:</span> <span className="text-[#46371c] font-medium">{order.store_name}</span></div>}
            <div><span className="text-[#75664d]">Total:</span> <span className="text-[#46371c] font-semibold">{formatPrice(order.total)}</span></div>
            <div><span className="text-[#75664d]">Deposito (50%):</span> <span className="text-[#46371c] font-semibold">{formatPrice(order.deposit_required)}</span></div>
            {order.delivery_address && <div className="sm:col-span-2"><span className="text-[#75664d]">Direccion:</span> <span className="text-[#46371c] font-medium">{order.delivery_address}</span></div>}
          </div>

          {order.items?.length > 0 && (
            <div className="mt-6 border-t border-[#e5dfd3] pt-4">
              <h4 className="font-heading text-lg font-semibold text-[#46371c] mb-3">Productos</h4>
              {order.items.map((item, i) => (
                <div key={i} className="flex justify-between py-2 border-b border-[#e5dfd3]/50 last:border-0 font-body text-sm">
                  <div>
                    <p className="text-[#46371c] font-medium">{item.name}</p>
                    <p className="text-xs text-[#75664d]">
                      {item.size || 'Estándar'} {item.filling ? `| ${item.filling}` : ''} {item.flavor ? `| ${item.flavor}` : ''}
                    </p>
                    {item.complements?.length > 0 && (
                      <p className="text-[11px] text-[#75664d]">
                        Adiciones: {item.complements.join(', ')}
                      </p>
                    )}
                  </div>
                  <span className="text-[#46371c] font-medium">{formatPrice(item.subtotal)}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
